/**
 * Retrieves a section using the Section Rendering API
 *
 * @param {String} url - URL of the section to return
 * @returns HTMLElement
 */
export const fetchSection = (url) =>
  fetch(url)
    .then((res) => res.text())
    .then((html) =>
      new DOMParser()
        .parseFromString(html, "text/html")
        .querySelector(".shopify-section")
    );


class ShopifyCartError {
  static async create(error, module) {
    const instance = new this(error, module);

    return instance.render(instance.message);
  }

  constructor(error, module) {
    this.message =
      error.description !== null ? error.description : error.message;
    this.status = error.status;

    this.module = module;
  }

  render(message) {
    const errors = document.querySelector(this.module.selectors.errors);

    const clone = document
      .querySelector(this.module.selectors.errorTemplate)
      .content.cloneNode(true);

    clone.querySelector(this.module.selectors.errorText).textContent = message;

    errors.appendChild(clone);
  }
}


class QuantityAdjuster extends HTMLElement {
  constructor() {
    super()

    this.input = this.querySelector('input')
    this.changeEvent = new Event('change', { bubbles: true })

    this.querySelectorAll('button').forEach(button =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    )
  }

  onButtonClick(e) {
    e.preventDefault()
    const button = e.target.closest('button')
    const previousValue = this.input.value

    button.name === 'plus' ? this.input.stepUp() : this.input.stepDown()
    if (previousValue !== this.input.value) {
      this.input.dispatchEvent(this.changeEvent)
    }
  }
}

customElements.define("quantity-adjuster", QuantityAdjuster);

class ShopifyCart extends HTMLElement {
  constructor() {
    super();

    window.shopifyCart = this;

    this.selectors = {
      form: '[data-shopify-cart-form]',
      cartItem: '[data-cart-item]',
      quantity: '[data-shopify-cart-quantity]',
      remove: '[data-shopify-cart-remove]',
      notify: '[data-shopify-cart-notify]',
      errors: '[data-shopify-cart-errors]',
      errorItem: '[data-shopify-cart-error]',
      errorText: '[data-shopify-cart-error-text]',
      errorClose: '[data-shopify-cart-error-close]',
      errorTemplate: '[data-shopify-cart-error-template]',
      productQuickview: '[data-product-quick-view]'
    };

    this.features = {
      gift: JSON.parse(this.dataset.cartFreeGiftEnable),
      shipping: JSON.parse(this.dataset.cartShippingProgressBarEnable),
      recommendations: JSON.parse(this.dataset.cartRecommendationsEnable),
    };

    try {
      this.giftSettings = JSON.parse(document.getElementById("giftWithPurchaseData")?.textContent || "{}");
    } catch (e) {
      console.error("Error parsing gift settings:", e);
      this.giftSettings = {};
    }

    this.recommendationsEl = this.querySelector("[data-cart-recommendations]");
  }

  connectedCallback() {
    this.initialse();
  }

  async initialse() {
    // Look for the cart data script specifically
    const cartScript = this.querySelector('script[type="application/json"]');
    
    if (!cartScript) {
      console.error('No cart data script found');
      return;
    }
    
    this.cartState = JSON.parse(cartScript.textContent);

    if (this.features.recommendations) {
      this.getRecommendations();
    }

    this.addEventListener('click', this.onRemoveClick.bind(this));
    this.addEventListener('change', this.onQuantityUpdate.bind(this));
    this.addEventListener('click', this.onCartErrorClick.bind(this));
    this.addEventListener('click', this.onUpdateClick.bind(this));

    document.addEventListener('submit', this.onFormSubmit.bind(this));
    document.addEventListener('click', this.onNotifyMeClick.bind(this));
    document.addEventListener('click', this.onQuickViewClick.bind(this));
    document.addEventListener('matching-products:add', () => {
      this.getCartSections();
    });

    const giftAdded = await this.checkAndAddGift();
    if (giftAdded) {
      const response = await this.retrieveSections();

      this.render(response);

      return
    }
    this.loading = false;
  }

  async getCartSections() {
    this.loading = true;

    const sectionIds = this.getSectionsToRender()
    .map((section) => section.section)
    .join(',');

    fetch(window.Shopify.routes.root + `?sections=${sectionIds}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })
    .then((response) => response.json())
    .then((response) => {
      const sections = this.getSectionsToRender()

      sections.forEach((section) => {
        const element = document.querySelector(section.selector);
  
        if (element) {
          element.innerHTML = this.getSectionInnerHTML(
            response[section.section],
            section.sectionSelector ? section.sectionSelector : '.shopify-section'
          );
        }

        this.loading = false;
      });
    })
    .catch((err) => {
      console.log('Error: ' + err)
      this.loading = false;
    });
  }

  async onFormSubmit(e) {
    if (!e.target.matches(this.selectors.form)) return;

    e.preventDefault();

    if (
      !document.body.classList.contains('template-cart') &&
      window.drawers.activeDrawer !== 'cart'
    ) {
      window.drawers.activeDrawer = 'cart';
    }

    this.loading = true;

    this.render(await this.addItem(e));

    const giftAdded = await this.checkAndAddGift();
    if (giftAdded) {
      const response = await this.retrieveSections();

      this.render(response);

      return
    }
    this.loading = false;
  }

  async onQuantityUpdate(e) {
    if (!e.target.matches(this.selectors.quantity)) return;

    // Only auto-update in cart drawer, not on cart page
    const isInCartDrawer = e.target.closest('.bbc-cart') !== null;
    if (!isInCartDrawer) {
      return; // On cart page, require manual UPDATE button click
    }

    e.preventDefault();

    this.loading = true;

    const item = e.target.closest(this.selectors.cartItem);

    const quantityEl = item.querySelector('[name="quantity"]');
    const quantity = quantityEl ? parseInt(quantityEl.value) : 1;
    
    const lineNumber = isInCartDrawer 
      ? Number(item.dataset.index) 
      : Number(item.dataset.index) + 1; 
    
    this.render(
      await this.updateItem({ line: lineNumber, quantity })
    );

    const giftAdded = await this.checkAndAddGift();
    if (giftAdded) {
      const response = await this.retrieveSections();

      this.render(response);

      return
    }
    this.loading = false;
  }

  async onRemoveClick(e) {
    if (e.target.closest(this.selectors.remove) === null) return;

    e.preventDefault();

    this.loading = true;

    const item = e.target.closest(this.selectors.cartItem);

    try {
      const isInCartDrawer = item.closest('.bbc-cart') !== null;
      
      const lineNumber = isInCartDrawer 
        ? Number(item.dataset.index) 
        : Number(item.dataset.index) + 1; 
      
      await this.updateItem({ line: lineNumber, quantity: 0 });
      // Refresh the page after successful removal, just like update does
      window.location.reload();
    } catch (error) {
      console.error('Remove failed:', error);
      this.loading = false;
    }
  }

  async onUpdateClick(e) {
    if (!e.target.closest('.js-CartUpdate_Update')) return;

    e.preventDefault();

    this.loading = true;

    const item = e.target.closest(this.selectors.cartItem);
    const quantityEl = item.querySelector('[name="quantity"]');
    const quantity = quantityEl ? parseInt(quantityEl.value) : 1;

    try {
      const isInCartDrawer = item.closest('.bbc-cart') !== null;
      
      const lineNumber = isInCartDrawer 
        ? Number(item.dataset.index) 
        : Number(item.dataset.index) + 1; 
      
      await this.updateItem({ line: lineNumber, quantity });
      // Refresh the page after successful update
      window.location.reload();
    } catch (error) {
      console.error('Update failed:', error);
      this.loading = false;
    }
  }

  async addItem(e) {
    const formData = new FormData(e.target);

    formData.append(
      'sections',
      this.getSectionsToRender().map((section) => section.section)
    );

    formData.append('sections_url', window.location.pathname);

    return fetch(window.routes.cart_add_url, {
      method: 'POST',
      headers: {
        Accept: 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: formData
    })
      .then(async (response) => {
        if (response.status !== 200) {
          throw response;
        }

        this.cartState = await this.getCart();
        return await response.json();
      })
      .catch((err) => {
        this.handleCartError(err);
      });
  }

  async updateItem(item) {
    return fetch(window.routes.cart_change_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/javascript'
      },
      body: JSON.stringify({
        ...item,
        sections: this.getSectionsToRender().map((section) => section.section),
        sections_url: window.location.pathname
      })
    })
      .then(async (response) => {
        if (response.status !== 200) {
          throw response;
        }

        this.cartState = await this.getCart();
        return await response.json();
      })
      .catch((err) => {
        this.handleCartError(err);
      });
  }

  async updateCart(update) {
    return fetch(window.routes.cart_update_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/javascript'
      },
      body: JSON.stringify({
        ...update,
        sections: this.getSectionsToRender().map((section) => section.section),
        sections_url: window.location.pathname
      })
    })
      .then(async (response) => {
        if (response.status !== 200) {
          throw response;
        }

        this.cartState = await this.getCart();
        return await response.json();
      })
      .catch((err) => {
        this.handleCartError(err);
      });
  }

  async addItems(items) {
    return fetch(`${window.routes.cart_add_url}`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        items,
        sections: this.getSectionsToRender().map((section) => section.section),
        sections_url: window.location.pathname
      })
    })
      .then(async (response) => {
        if (response.status !== 200) {
          throw response;
        }

        this.cartState = await this.getCart();
        return response.json();
      })
      .catch((err) => {
        this.handleCartError(err);
      });
  }

  async getCart() {
    return fetch('/cart.js', {
      method: 'GET',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Content-Type': 'application/json'
      }
    }).then(async (response) => {
      const responseJson = await response.json();
      return responseJson;
    });
  }

  async getRecommendations(id = this.cartState.items[0].product_id) {
    fetchSection(
      `${window.routes.product_recommendations_url}?section_id=cart-upsell-slider&product_id=${id}&intent=related`
    ).then((section) => {
      if (section.querySelector("[data-recommended-products]") !== null) {
        this.recommendationsEl.innerHTML = section.innerHTML;
        this.createCarousel();
      }
    });
  }

  async handleCartError(err) {
    this.loading = false;
    this.error = true;

    if ('json' in err) {
      return ShopifyCartError.create(await err.json(), this);
    }

    return ShopifyCartError.create(err, this);
  }

  async retrieveSections() {
    const response = await fetch(`/?sections=${this.getSectionsToRender().map(
      (section) => section.section
    ).join(',')}`);
    const sections = await response.json();
    return { sections };
  }

  createCarousel() {
    this.swiper = new Swiper(this.recommendationsEl.querySelector('.swiper'), {
      slidesPerView: 1.95,
      spaceBetween: 10,
      breakpoints: {
        768: {
          slidesPerView: 2.5,
          spaceBetween: 5
        }
      }
    });
    const triggers = this.querySelectorAll('[data-overlay-trigger]');
  
    triggers.forEach(trigger => {  
      trigger.addEventListener('click', function () {
        document.querySelector(`[data-cart-product-overlay="${trigger.dataset.overlayTrigger}"]`).classList.toggle('prd-Card_Overlay-active');
      });
    });
  }

  onCartErrorClick(e) {
    if (e.target.closest(this.selectors.errorClose) === null) return;

    this.destroyErrors();
  }

  async destroyErrors() {
    this.error = false;

    this.querySelectorAll('[data-shopify-cart-error]').forEach((error) =>
      error.remove()
    );

    this.render(
      await this.updateCart({
        attributes: { update: true }
      })
    );
  }

  onNotifyMeClick(e) {
    if (e.target.closest(this.selectors.notify) === null) return;

    new NotifyMe(
      e.target.closest(this.selectors.notify).dataset.shopifyCartNotify
    );
  }

  onQuickViewClick(e) {
    if (e.target.closest(this.selectors.productQuickview) === null) return;

    new ProductQuickview(
      e.target.closest(this.selectors.productQuickview).dataset.productQuickView
    );
  }

  async render(state, sections = this.getSectionsToRender()) {
    if (!state) return;
    
    sections.forEach((section) => {
      const element = document.querySelector(section.selector);
      if (element) {
        const sectionContent = state.sections[section.section];
        if (sectionContent) {
          element.innerHTML = this.getSectionInnerHTML(
            sectionContent,
            section.sectionSelector ? section.sectionSelector : '.shopify-section'
          );
        }
      }
    });

    await this.getRecommendations();

    this.loading = false;
  }

  getSectionsToRender() {
    let sections = [
      {
        section: "cart-items",
        selector: "[data-cart-items]",
      },
      {
        section: "cart-count",
        selector: "[data-slide-cart-count]",
      },
      {
        section: "cart-count-nav",
        selector: "[data-nav-cart-count]",
      },
      {
        section: "cart-totals",
        selector: "[data-cart-totals]",
      },
    ];

    // Add cart page specific selectors
    if (document.body.classList.contains('template-cart')) {
      sections.push({
        section: "cart-products",
        selector: ".crt-Products",
      });
    }

    if (this.features.shipping) {
      sections.push({
        section: "cart-shipping-progress-bar",
        selector: "[data-cart-shipping-progress-bar]",
      });
    }

    // if (this.features.gift) {
    //   sections.push({
    //     section: "cart-free-gift-progress-bar",
    //     selector: "[data-cart-free-gift-progress-bar]",
    //   });
    // }

    return sections;
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  async checkAndAddGift() {
    this.loading = true;
    this.cartState = await this.getCart();
    return new Promise((resolve) => {
      const { enabled, giftVariantId, minimumSpend } = this.giftSettings || {};

      if (!enabled || !giftVariantId || !this.cartState) {
        resolve(false);
        return;
      }

      const cartTotal = this.cartState.total_price / 100;
      const minSpend = parseFloat(minimumSpend);

      // Only add gift if we meet minimum spend and don't already have it
      const giftInCart = this.cartState.items.some((item) => item.variant_id === giftVariantId);

      if (cartTotal >= minSpend && !giftInCart) {
        this.addItems([
          {
            id: giftVariantId,
            quantity: 1,
            properties: {
              _gift_with_purchase: true,
            },
          },
        ])
        .then(() => resolve(true))
          .catch(() => resolve(false));
      } else if (cartTotal < minSpend && giftInCart) {
        this.updateItem({ id: `${giftVariantId}`, quantity: 0 })
        .then(() => resolve(true))
          .catch(() => resolve(false));
      }
      else {
        resolve(false);
      }
    });
  }

  set loading(val) {
    this.classList.toggle('loading', val);
  }

  set error(val) {
    this.classList.toggle('error', val);
  }
}

customElements.define("shopify-cart", ShopifyCart);

class NotifyMe {
  constructor(handle) {
    this.handle = handle;

    this.initialise();
  }

  async initialise() {
    this.popup = new Popup();

    const html = await fetchSection(
      `/products/${this.handle}?section_id=notify-me`
    ).then((section) => section.innerHTML);

    this.popup.insertContent(html);

    this.popup.el.addEventListener("submit", this.onFormSubmit.bind(this));
  }

  /**
   * Form submit handler
   *
   * @param {Event} e
   */
  onFormSubmit(e) {
    if (!e.target.matches("[data-shopify-cart-notify-form]")) return;

    e.preventDefault();

    e.target.querySelector('button[type="submit"]').innerHTML = `
        <svg class="w-5 h-5 text-black animate-spin" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
          <g fill="currentColor"><g><path d="M24 47a23 23 0 1 1 23-23 23.025 23.025 0 0 1-23 23zm0-42a19 19 0 1 0 19 19A19.021 19.021 0 0 0 24 5z" opacity=".4"></path><path d="M47 24h-4A19.021 19.021 0 0 0 24 5V1a23.025 23.025 0 0 1 23 23z"></path></g></g>
        </svg>
      `;

    this.createNotification(e.target);
  }

  /**
   * Creates the notification with Back in stock
   *
   * @param {Element} form
   */
  createNotification(form) {
    const formData = new FormData(form);

    BIS.create(
      formData.get("email"),
      formData.get("variant_id"),
      formData.get("product_id"),
      {
        accepts_marketing: form.querySelector('input[name="accepts_marketing"]')
          .checked,
      }
    ).then((response) => {
      if (response.status === "OK") {
        this.popup.replaceContent(`
            <h3 class="mb-2 text-xl md:text-2xl">Success</h3>
            <p>${response.message}</p>
          `);
      } else {
        const errors = response.errors;

        this.popup.replaceContent(`
            <h3 class="mb-2 text-xl md:text-2xl">Error</h3>
  
            ${Object.keys(errors)
              .map((key) => {
                return `<p class="mb-2">${errors[key][0]}</p>`;
              })
              .join("")}
  
            <button type="button" class="flex items-center mt-4 justify-center p-2 font-medium text-gray-800 bg-gray-200 rounded" data-popup-close>Close</button>
          `);
      }
    });
  }
}

class ProductQuickview {
  constructor(handle) {
    this.handle = handle;

    this.initialise();
  }

  async initialise() {
    this.popup = new Popup();

    const html = await fetchSection(
      `/products/${this.handle}?section_id=product-quickview`
    ).then((section) => section.innerHTML);

    this.popup.insertContent(html);

    if (window.shopifyWishlist) {
      window.shopifyWishlist.initButtons(this.popup.el);
    }
  }
}
